import axios from 'axios';
import fileDownload from 'js-file-download';
import * as endpoint from '../endpoint';
import * as actionTypes from '../actionTypes';
import messages from '../../lib/i18n';
import {
  consolelog,
  addCancelToken,
  delCancelToken,
  cancelAllApi,
  convertSearchOptions,
  checkCancelToken,
  postMessageForCansuke,
  consoleNormalLog,
} from '../../lib/tools';
import config from '../../config';
import {
  EXCLUSION_API,
  PAGE_PATH,
  REMOTE_MEMBER_STATUS,
} from '../../lib/constants';
import {
  ACTIVATE_ERROR_CODE,
  UNAUTHORIZED_PREFIX,
} from '../../lib/apiErrorCodes';
import flattenMessages from '../../functions/flattenMessages';

/**
 * 指定されたIDに基づいてaxiosのキャンセルトークンとキーを生成する。
 *
 * @param {string} id - キャンセルトークンを識別するためのID
 * @returns {Object}
 * @returns {axios.CancelToken} return.cancelToken - axiosのキャンセルトークン。
 * @returns {string} return.cancelTokenKey - キャンセルトークンの一意のキー。
 *
 * @example
 * const { cancelToken, cancelTokenKey } = createCancelToken('getGroups');
 */
export const createCancelToken = id => {
  const cancelToken = new axios.CancelToken(c => c);
  const cancelTokenKey = `${id}_${Math.floor(Math.random() * 100000000)}`;
  const exclusionApis = EXCLUSION_API[id] || [];
  if (exclusionApis && exclusionApis.length > 0) {
    exclusionApis.forEach(apiName => {
      Object.keys(window.apiToken).forEach(key => {
        if (key.indexOf(`${apiName}_`) === 0) {
          delCancelToken(key);
        }
      });
    });
  }
  addCancelToken(cancelTokenKey, cancelToken);
  return { cancelToken, cancelTokenKey };
};

const isTestEnvironment = process.env.NODE_ENV === 'test';
const { locale } = isTestEnvironment ? { locale: 'ja' } : config;
export const i18nMessages = flattenMessages(messages[locale]);

export const handleErrorResponseType = {
  tags: 'tags',
  trash: 'trash',
  group: 'group',
};

/**
 * APIからのエラー応答に基づいて適切なactionをdispatchする関数
 * HTTPステータスコードに応じてユーザーに表示するプロンプトを設定し、
 * 必要に応じてページ遷移を行う。
 *
 * @param {Object} thrown - axiosリクエストから返された例外オブジェクト。
 * @param {function} dispatch - Redux dispatch関数
 * @param {string} [dispatchType=''] - 特定のエラーメッセージの種類を指定するオプションのパラメータ。
 * @param {string} [additionalMessage=''] - 追加のエラーメッセージを指定するオプションのパラメータ。
 */
export const handleErrorResponse = (
  thrown,
  dispatch,
  dispatchType = '',
  additionalMessage = '',
) => {
  const buttonClose = i18nMessages['userPrompt.button.close'];
  const buttonRetry = i18nMessages['userPrompt.button.retry'];
  const close = [{ name: buttonClose, type: 1 }];
  const retry = [{ name: buttonRetry, type: 1 }];
  const httpStatus = thrown.response && thrown.response.status;
  switch (httpStatus) {
    case 400: {
      const outMessage = additionalMessage
        ? [additionalMessage, i18nMessages['userPrompt.message.status400']]
        : i18nMessages['userPrompt.message.status400'];
      dispatch({
        type: actionTypes.OPEN_USER_PROMPT,
        promptMessage: outMessage,
        buttonArray: close,
      });
      break;
    }
    case 401: {
      // セッション(APIトークン)の有効期限が切れたのでAPIがエラーになっている
      window.location.href = PAGE_PATH.DS_LOGOUT;
      break;
    }
    case 404: {
      const outMessage = additionalMessage
        ? [additionalMessage, i18nMessages['userPrompt.message.status404']]
        : i18nMessages['userPrompt.message.status404'];
      dispatch({
        type: actionTypes.OPEN_USER_PROMPT,
        promptMessage: outMessage,
        buttonArray: close,
      });
      break;
    }
    case 409: {
      let outMessage;
      switch (dispatchType) {
        case handleErrorResponseType.tags: {
          outMessage = [
            i18nMessages['userPrompt.message.tags.status409.1'],
            i18nMessages['userPrompt.message.tags.status409.2'],
          ];
          break;
        }
        case handleErrorResponseType.trash: {
          outMessage = [
            i18nMessages['userPrompt.message.trash.status409.1'],
            i18nMessages['userPrompt.message.trash.status409.2'],
          ];
          break;
        }
        case handleErrorResponseType.group: {
          outMessage = [
            i18nMessages['userPrompt.message.group.nameConflict.1'],
            i18nMessages['userPrompt.message.group.nameConflict.2'],
          ];
          break;
        }
        default: {
          return;
        }
      }
      dispatch({
        type: actionTypes.OPEN_USER_PROMPT,
        promptMessage: outMessage,
        buttonArray: close,
      });
      break;
    }
    case 500: {
      const outMessage = additionalMessage
        ? [additionalMessage, i18nMessages['userPrompt.message.status500']]
        : i18nMessages['userPrompt.message.status500'];
      dispatch({
        type: actionTypes.OPEN_USER_PROMPT,
        promptMessage: outMessage,
        buttonArray: close,
      });
      break;
    }
    default: {
      const { code, message, name, stack } = thrown;
      consoleNormalLog(
        `[**handleErrorResponse**]code=${code},message=${message},name=${name},stack=${stack}`,
      );
      if (
        thrown &&
        thrown.message &&
        thrown.message.includes('Network Error')
      ) {
        // サーバが落ちている場合など応答がないときにエラーに
        const outMessage = [
          additionalMessage,
          i18nMessages['userPrompt.message.serverError'],
        ];
        dispatch({
          type: actionTypes.OPEN_USER_PROMPT,
          promptMessage: outMessage,
          buttonArray: retry,
        });
        return;
      }
      // その他のエラーの場合（SUCCESS_API_XXXXの処理内でエラーも含む）
      const outMessage = additionalMessage
        ? [additionalMessage, i18nMessages['userPrompt.message.otherError']]
        : i18nMessages['userPrompt.message.otherError'];
      dispatch({
        type: actionTypes.OPEN_USER_PROMPT,
        promptMessage: outMessage,
        buttonArray: close,
      });
      break;
    }
  }
};
export const getVersion = () => {
  consolelog('[API CALL] getVersion()');
  return endpoint.VERSION;
};

export const ciosv2Authorize = provider => {
  consolelog('[API CALL] ciosv2Authorize()');
  window.location.href = endpoint.CIOSV2_AUTHORIZE;
  if (provider) {
    window.location.href = `${endpoint.CIOSV2_AUTHORIZE}?provider=${provider}`;
  }
  return { type: actionTypes.DEFAULT };
};

export const ciosv2Logout = apiToken => dispatch => {
  consolelog('[API CALL] ciosv2Logout()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.CIOSV2_LOGOUT;
  const { cancelToken, cancelTokenKey } = createCancelToken('ciosv2Logout');
  const params = {
    cancelToken,
  };
  return axios
    .get(url, params)
    .then(() => {
      delCancelToken(cancelTokenKey);
      dispatch({ type: actionTypes.LOGOUT_DONE });
      cancelAllApi();
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        dispatch({ type: actionTypes.LOGOUT_DONE });
        cancelAllApi();
      }
    });
};

export const getOperatorWorkRecords =
  (apiToken, searchOptions = {}) =>
  dispatch => {
    consolelog('[API CALL] getOperatorWorkRecords()', searchOptions);
    axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
    axios.defaults.responseType = 'json';
    const url = endpoint.API_GET_WORK_RECORDS;
    const { cancelToken, cancelTokenKey } = createCancelToken(
      'getOperatorWorkRecords',
    );
    const convertedSearchOptions = convertSearchOptions(searchOptions);
    const params = {
      ...convertedSearchOptions,
      cancelToken,
    };
    return axios
      .post(url, params)
      .then(res => {
        if (checkCancelToken(cancelTokenKey)) {
          dispatch({
            type: 'SUCCESS_API_GET_OPERATOR_WORK_RECORDS',
            payload: res.data,
          });
        }
        delCancelToken(cancelTokenKey);
      })
      .catch(thrown => {
        delCancelToken(cancelTokenKey);
        if (!axios.isCancel(thrown)) {
          handleErrorResponse(thrown, dispatch);
        }
      });
  };

export const updateWorkRecordStatus = (apiToken, id, status) => dispatch => {
  consolelog('[API CALL] updateWorkRecordStatus()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_UPDATE_WORK_RECORD_STATUS;
  const { cancelToken, cancelTokenKey } = createCancelToken(
    'updateWorkRecordStatus',
  );
  const params = {
    id,
    status,
    cancelToken,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_UPDATE_WORK_RECORD_STATUS,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

export const deleteWorkRecords =
  (apiToken, apiParams, responses) => dispatch => {
    consolelog('[API CALL] deleteWorkRecords()');
    axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
    axios.defaults.responseType = 'json';
    const url = endpoint.API_UPDATE_STATUS_WORK_RECORDS;
    const { cancelToken, cancelTokenKey } =
      createCancelToken('deleteWorkRecords');
    const params = {
      ...apiParams,
      cancelToken,
      data: {
        ...apiParams,
        cancelToken,
      },
    };
    return axios
      .delete(url, params)
      .then(res => {
        delCancelToken(cancelTokenKey);
        if (responses) {
          responses.forEach(response =>
            dispatch({ type: response, payload: res.data }),
          );
        }
      })
      .catch(thrown => {
        delCancelToken(cancelTokenKey);
        if (!axios.isCancel(thrown)) {
          handleErrorResponse(thrown, dispatch);
        }
      });
  };

export const enableWorkRecords =
  (apiToken, responseAction, apiParams) => dispatch => {
    consolelog('[API CALL] enableWorkRecords()');
    axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
    axios.defaults.responseType = 'json';
    const url = endpoint.API_UPDATE_STATUS_WORK_RECORDS;
    const { cancelToken, cancelTokenKey } =
      createCancelToken('enableWorkRecords');
    const params = {
      ...apiParams,
      cancelToken,
    };
    return axios
      .patch(url, params)
      .then(res => {
        delCancelToken(cancelTokenKey);
        dispatch({ type: responseAction, payload: res.data });
      })
      .catch(thrown => {
        delCancelToken(cancelTokenKey);
        if (!axios.isCancel(thrown)) {
          handleErrorResponse(thrown, dispatch, handleErrorResponseType.trash);
        }
      });
  };

export const completeDeleteWorkRecords =
  (apiToken, responseAction, apiParams) => dispatch => {
    consolelog('[API CALL] completeDeleteWorkRecords()');
    axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
    axios.defaults.responseType = 'json';
    const url = endpoint.API_DELETE_WORK_RECORDS;
    const { cancelToken, cancelTokenKey } = createCancelToken(
      'completeDeleteWorkRecords',
    );
    const params = {
      data: {
        ...apiParams,
        cancelToken,
      },
    };
    return axios
      .delete(url, params)
      .then(res => {
        delCancelToken(cancelTokenKey);
        dispatch({ type: responseAction, payload: res.data });
      })
      .catch(thrown => {
        delCancelToken(cancelTokenKey);
        if (!axios.isCancel(thrown)) {
          handleErrorResponse(thrown, dispatch);
        }
      });
  };

export const getUserInfo = apiToken => dispatch => {
  consolelog('[API CALL] getUserInfo()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_GET_USER_INFO;
  const { cancelToken, cancelTokenKey } = createCancelToken('getUserInfo');
  const params = {
    cancelToken,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_GET_USER_INFO,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

export const getQR = (apiToken, codeId, expires) => dispatch => {
  consolelog('[API CALL] getQR()', codeId, expires);
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_GET_QR;
  const { cancelToken, cancelTokenKey } = createCancelToken('getQR');
  const params = {
    codeId,
    expires,
    cancelToken,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_GET_QR,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

export const listQRs = (apiToken, id) => dispatch => {
  consolelog('[API CALL] listQRs()', id);
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_LIST_QRS;
  const { cancelToken, cancelTokenKey } = createCancelToken('listQRs');
  const params = {
    id,
    cancelToken,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_LIST_QRS,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

export const updateQR = (apiToken, codeId, expiresAt) => dispatch => {
  consolelog('[API CALL] updateQR()', codeId, expiresAt);
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_UPDATE_QR;
  const { cancelToken, cancelTokenKey } = createCancelToken('updateQR');
  const params = {
    codeId,
    expiresAt,
    cancelToken,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_UPDATE_QR,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

export const downloadMediaFiles =
  (apiToken, ids, groupId, responses) => dispatch => {
    consolelog('[API CALL] downloadMediaFiles()');
    axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
    axios.defaults.responseType = 'blob';
    const url = endpoint.API_DOWNLOAD_MEDIA_FILES;
    const { cancelToken, cancelTokenKey } =
      createCancelToken('downloadMediaFiles');
    const params = {
      cancelToken,
      ids,
      groupId,
    };
    dispatch({ type: actionTypes.REQUEST_API_DOWNLOAD_MEDIA_FILES });
    return axios
      .post(url, params)
      .then(res => {
        delCancelToken(cancelTokenKey);
        const line =
          res.headers['content-disposition'] || 'filename=sample.zip';
        const filename = line.split('=')[1];
        fileDownload(res.data, filename);
        dispatch({
          type: actionTypes.SUCCESS_API_DOWNLOAD_MEDIA_FILES,
          payload: res.data,
        });
        if (responses) {
          responses.forEach(response => {
            dispatch({ type: response, payload: res.data });
          });
        }
      })
      .catch(thrown => {
        delCancelToken(cancelTokenKey);
        if (!axios.isCancel(thrown)) {
          handleErrorResponse(thrown, dispatch);
        }
      });
  };

export const downloadCheckResultCsv =
  (apiToken, ids, responses) => dispatch => {
    consolelog('[API CALL] downloadCheckResult()');
    axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
    axios.defaults.responseType = 'blob';
    const url = endpoint.API_DOWNLOAD_CHECK_RESULT_CSV;
    const { cancelToken, cancelTokenKey } = createCancelToken(
      'downloadCheckResult',
    );
    const params = {
      cancelToken,
      ids,
    };
    dispatch({ type: actionTypes.REQUEST_API_DOWNLOAD_MEDIA_FILES });
    return axios
      .post(url, params)
      .then(res => {
        delCancelToken(cancelTokenKey);
        const filename = res.headers['content-disposition'].split('=')[1];
        fileDownload(res.data, filename);
        dispatch({
          type: actionTypes.SUCCESS_API_DOWNLOAD_MEDIA_FILES,
          payload: res.data,
        });
        if (responses) {
          responses.forEach(response => {
            dispatch({ type: response, payload: res.data });
          });
        }
      })
      .catch(thrown => {
        delCancelToken(cancelTokenKey);
        if (!axios.isCancel(thrown)) {
          handleErrorResponse(thrown, dispatch);
        }
      });
  };

export const postWorkRecordUrls = (apiToken, ids, responses) => dispatch => {
  consolelog('[API CALL] postWorkRecordUrls()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_GET_WORK_RECORD_URLS;
  const { cancelToken, cancelTokenKey } =
    createCancelToken('postWorkRecordUrls');
  const params = {
    cancelToken,
    ids,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      if (responses) {
        responses.forEach(response =>
          dispatch({ type: response, payload: res.data }),
        );
      }
      postMessageForCansuke(res.data);
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(
          thrown.response && thrown.response.status,
          dispatch,
        );
      }
    });
};

export const onDownloadUncompressed = (apiToken, id, groupId) => dispatch => {
  consolelog('[API CALL] onDownloadUncompressed()', id);
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'blob';
  const url = endpoint.API_DOWNLOAD_ONE_AS_UNCOMPRESSED;
  const { cancelToken, cancelTokenKey } = createCancelToken(
    'onDownloadUncompressed',
  );
  const params = {
    cancelToken,
    params: { id, groupId },
  };
  return axios
    .get(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_DOWNLOAD_MEDIA_FILES,
        payload: res.data,
      });
      const line = res.headers['content-disposition'] || 'filename=sample.zip';
      const filename = decodeURI(line.split('=')[1].replaceAll('+', '%20'));
      fileDownload(res.data, filename);
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};
export const analyze = (apiToken, workRecordIds) => dispatch => {
  consolelog('[API CALL] analyze()', workRecordIds);
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_ANALYZE;
  const { cancelToken, cancelTokenKey } = createCancelToken('analyze');
  const params = {
    cancelToken,
    workRecordIds,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      if (res.data && res.data.result) {
        dispatch({
          type: actionTypes.SUCCESS_API_ANALYZE,
          payload: res.data,
        });
      } else {
        dispatch({
          type: actionTypes.ERROR_API_ANALYZE,
          payload: res.data,
        });
      }
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

export const syncOperators = apiToken => dispatch => {
  consolelog('[API CALL] syncOperators()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_SYNC_OPERATORS;
  const { cancelToken, cancelTokenKey } = createCancelToken('syncOperators');
  const params = {
    cancelToken,
  };
  return axios
    .patch(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      if (res.data && res.data.result) {
        const buttonClose = i18nMessages['userPrompt.button.close'];
        const close = [{ name: buttonClose, type: 1 }];
        const successMessage =
          i18nMessages['staff.title.sync.authority.success'];
        dispatch({
          type: actionTypes.OPEN_USER_PROMPT,
          promptMessage: successMessage,
          buttonArray: close,
        });
      }
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        if (thrown.response.status === 403) {
          const buttonClose = i18nMessages['userPrompt.button.close'];
          const close = [{ name: buttonClose, type: 1 }];
          const errorMessage =
            i18nMessages['staff.title.sync.authority.failure.403'];
          dispatch({
            type: actionTypes.OPEN_USER_PROMPT,
            promptMessage: errorMessage,
            buttonArray: close,
          });
        } else {
          handleErrorResponse(thrown, dispatch);
        }
      }
    });
};

export const updateCompanies = (apiToken, apiParams) => dispatch => {
  consolelog('[API CALL] updateCompanies()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_UPDATE_COMPANIES;
  const { cancelToken, cancelTokenKey } = createCancelToken('updateCompanies');
  const params = {
    cancelToken,
    companies: apiParams,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_UPDATE_COMPANIES,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

export const getGeocode = (apiToken, address) => dispatch => {
  consolelog('[API CALL] getGeocode()', address);
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_GET_GEOCODE;
  const { cancelToken, cancelTokenKey } = createCancelToken('getGeocode');
  const params = {
    address,
    cancelToken,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_GET_GEOCODE,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

export const releaseNumber = apiToken => dispatch => {
  consolelog('[API CALL] releaseNumber()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_APP_RELEASE_NUMBER;
  const { cancelToken, cancelTokenKey } = createCancelToken('releaseNumber');
  const params = {
    cancelToken,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_APP_RELEASE_NUMBER,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

/** ルーム作成 */
export const createRoom = (apiToken, createRoomParams) => dispatch => {
  consolelog('[API CALL] createRoom()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_CREATE_ROOM;
  const { cancelToken, cancelTokenKey } = createCancelToken('createRoom');
  const params = {
    cancelToken,
    ...createRoomParams,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_CREATE_ROOM,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};
/** ログインユーザがメンバーのルームリストを取得する */
export const getCallingRooms = apiToken => dispatch => {
  consolelog('[API CALL] getCallingRooms()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_GET_NO_JOINED_ROOMS;
  const { cancelToken, cancelTokenKey } = createCancelToken('getCallingRooms');
  const params = { cancelToken, params: { order: 'DESC' } };
  return axios
    .get(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_GET_NO_JOINED_ROOMS,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};
/** ルーム入室情報取得 */
export const getEnterRoomInfo = (apiToken, appParams) => dispatch => {
  consolelog('[API CALL] getEnterRoomInfo()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const { appRoomId, groupId } = appParams;
  const url = endpoint.API_GET_ENTER_ROOM_INFO.replace(
    '{appRoomId}',
    appRoomId,
  );
  const { cancelToken, cancelTokenKey } = createCancelToken('getEnterRoomInfo');
  const params = { cancelToken, status: REMOTE_MEMBER_STATUS.joining, groupId };
  return axios
    .patch(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_GET_ENTER_ROOM_INFO,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};
/** ルーム情報取得 */
export const getRoomInfo = (apiToken, appRoomId) => dispatch => {
  consolelog('[API CALL] getRoomInfo()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_GET_ROOM_INFO.replace('{appRoomId}', appRoomId);
  const { cancelToken, cancelTokenKey } = createCancelToken('getRoomInfo');
  const params = { cancelToken };
  return axios
    .get(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_GET_ROOM_INFO,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

/** ルームメンバー取得 */
export const getCallingRoomMembers = (apiToken, appRoomIds) => dispatch => {
  consolelog('[API CALL] getCallingRoomMembers()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  appRoomIds.map(appRoomId => {
    const url = endpoint.API_GET_ROOM_INFO.replace('{appRoomId}', appRoomId);
    const { cancelToken, cancelTokenKey } = createCancelToken(
      'getCallingRoomMembers',
    );
    const params = { cancelToken };
    return axios
      .get(url, params)
      .then(res => {
        delCancelToken(cancelTokenKey);
        dispatch({
          type: actionTypes.SUCCESS_API_GET_CALLING_ROOM_MEMBERS,
          payload: res.data,
        });
      })
      .catch(thrown => {
        delCancelToken(cancelTokenKey);
        if (!axios.isCancel(thrown)) {
          handleErrorResponse(thrown, dispatch);
        }
      });
  });
};

/** 遠隔支援再呼び出し処理 */
export const recallRoomMember = (apiToken, appParams) => dispatch => {
  consolelog('[API CALL] recallRoomMember()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const { appRoomId, operatorId } = appParams;
  const url = endpoint.API_RECALL_ROOM_MEMBER.replace(
    '{appRoomId}',
    appRoomId,
  ).replace('{operatorId}', operatorId);
  const { cancelToken, cancelTokenKey } = createCancelToken('recallRoomMember');
  const params = { cancelToken };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_RECALL_ROOM_MEMBER,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

export const updateMemberStatus = (apiToken, appRoomId) => dispatch => {
  consolelog('[API CALL] updateMemberStatus()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_UPDATE_MEMBER_STATUS;
  const { cancelToken, cancelTokenKey } =
    createCancelToken('updateMemberStatus');
  const status = 1;
  const params = {
    cancelToken,
    appRoomId,
    status,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_UPDATE_MEMBER_STATUS,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};
export const getUserDevices = (apiToken, operatorId) => dispatch => {
  consolelog('[API CALL] getUserDevices()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_GET_USER_DEVICES;
  const { cancelToken, cancelTokenKey } = createCancelToken('getUserDevices');
  const params = {
    cancelToken,
    operatorId,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_GET_USER_DEVICES,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

export const updateUserDeviceInfo = (apiToken, apiParams) => dispatch => {
  consolelog('[API CALL] updateUserDeviceInfo()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_UPDATE_USER_DEVICE_INFO;
  const { cancelToken, cancelTokenKey } = createCancelToken(
    'updateUserDeviceInfo',
  );
  const params = {
    cancelToken,
    ...apiParams,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_UPDATE_USER_DEVICE_INFO,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};
export const deleteRoom = (apiToken, roomId) => dispatch => {
  consolelog('[API CALL] deleteRoom()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_DELETE_ROOM;
  const { cancelToken, cancelTokenKey } = createCancelToken('deleteRoom');
  const params = {
    cancelToken,
    roomId,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_DELETE_ROOM,
        payload: res.data,
      });
    })
    .catch(thrown => {
      const httpStatus = thrown.response && thrown.response.status;
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown) && httpStatus !== 423) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};
export const deleteRoomByDevice =
  (apiToken, operatorId, deviceId) => dispatch => {
    consolelog('[API CALL] deleteRoomByDevice()');
    axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
    axios.defaults.responseType = 'json';
    const url = endpoint.API_DELETE_ROOM_BY_DEVICE;
    const { cancelToken, cancelTokenKey } =
      createCancelToken('deleteRoomByDevice');
    const params = {
      cancelToken,
      operatorId,
      deviceId,
    };
    return axios
      .post(url, params)
      .then(res => {
        delCancelToken(cancelTokenKey);
        dispatch({
          type: actionTypes.SUCCESS_API_DELETE_ROOM,
          payload: res.data,
        });
      })
      .catch(thrown => {
        delCancelToken(cancelTokenKey);
        if (!axios.isCancel(thrown)) {
          handleErrorResponse(thrown, dispatch);
        }
      });
  };

export const activate = (apiToken, activationCode) => dispatch => {
  consolelog('[API CALL] activate()', activationCode);
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_ACTIVATE;
  const { cancelToken, cancelTokenKey } = createCancelToken('activate');
  const params = { cancelToken, activationCode };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      const type = res.data.result
        ? actionTypes.SUCCESS_API_ACTIVATE
        : actionTypes.ERROR_API_ACTIVATE;
      dispatch({ type, payload: res.data });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        const response = thrown.response || {};
        const errorCode = response && response.data && response.data.errorCode;
        if (
          errorCode === `${UNAUTHORIZED_PREFIX}${ACTIVATE_ERROR_CODE.noLicense}`
        ) {
          window.location.href = `${PAGE_PATH.LOGIN_FAILURE}/${ACTIVATE_ERROR_CODE.noLicense}`;
        } else {
          handleErrorResponse(thrown, dispatch);
        }
      }
    });
};

export const getBlackboardList = (apiToken, apiParams) => dispatch => {
  consolelog('[API CALL] getBlackboardList()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_GET_BLACKBOARD_LIST;
  const { cancelToken, cancelTokenKey } =
    createCancelToken('getBlackboardList');
  const params = {
    cancelToken,
    ...apiParams,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_GET_BLACKBOARD_PRESET_LIST,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

export const getBlackboard = (apiToken, id) => dispatch => {
  consolelog('[API CALL] getBlackboard()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_GET_BLACKBOARD_LIST;
  const { cancelToken, cancelTokenKey } = createCancelToken('getBlackboard');
  const params = {
    cancelToken,
    id,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_GET_BLACKBOARD_PRESET_FOR_REMOTE,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

export const createBlackboard = (apiToken, apiParams) => dispatch => {
  consolelog('[API CALL] createBlackboard()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_CREATE_BLACKBOARD;
  const { cancelToken, cancelTokenKey } = createCancelToken('createBlackboard');
  const params = {
    cancelToken,
    ...apiParams,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_CREATE_BLACKBOARD,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};
export const updateBlackboard = (apiToken, apiParams) => dispatch => {
  consolelog('[API CALL] updateBlackboard()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const { id, ...rest } = apiParams;
  const url = `${endpoint.API_BLACKBOARD_BASE}/${id}`;
  const { cancelToken, cancelTokenKey } = createCancelToken('updateBlackboard');
  const params = {
    cancelToken,
    ...rest,
  };
  return axios
    .put(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_UPDATE_BLACKBOARD,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};
export const deleteBlackboard = (apiToken, id) => dispatch => {
  consolelog('[API CALL] deleteBlackboard()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = `${endpoint.API_BLACKBOARD_BASE}/${id}`;
  const { cancelToken, cancelTokenKey } = createCancelToken('deleteBlackboard');
  const params = {
    cancelToken,
  };
  return axios
    .delete(url, params)
    .then(() => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_DELETE_BLACKBOARD,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

// 表示対象企業選択用API
export const getUserCompanies = apiToken => dispatch => {
  consolelog('[API CALL] getUserCompanies()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_GET_USER_COMPANIES;
  const { cancelToken, cancelTokenKey } = createCancelToken('getUserCompanies');
  const params = { cancelToken };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_GET_USER_COMPANIES,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

// お気に入り
export const getFavorites = (apiToken, apiParams) => dispatch => {
  consolelog('[API CALL] getFavorites()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_GET_FAVORITES;
  const { cancelToken, cancelTokenKey } = createCancelToken('getFavorites');
  const params = { cancelToken, params: apiParams };
  return axios
    .get(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_GET_FAVORITES,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

export const createFavorite = (apiToken, apiParams) => dispatch => {
  consolelog('[API CALL] createFavorite()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_CREATE_FAVORITE;
  const { cancelToken, cancelTokenKey } = createCancelToken('createFavorite');
  const params = {
    cancelToken,
    ...apiParams,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_CREATE_FAVORITE,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

export const deleteFavorite = (apiToken, id) => dispatch => {
  consolelog('[API CALL] deleteFavorite()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_DELETE_FAVORITE.replace('{id}', id);
  const { cancelToken, cancelTokenKey } = createCancelToken('deleteFavorite');
  const params = { cancelToken };
  return axios
    .delete(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_DELETE_FAVORITE,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};

export const updateFavorite = (apiToken, id, apiParams) => dispatch => {
  consolelog('[API CALL] updateFavorite()');
  axios.defaults.headers.common.Authorization = `Bearer ${apiToken}`;
  axios.defaults.responseType = 'json';
  const url = endpoint.API_DELETE_FAVORITE.replace('{id}', id);
  const { cancelToken, cancelTokenKey } = createCancelToken('updateFavorite');
  const params = {
    cancelToken,
    ...apiParams,
  };
  return axios
    .post(url, params)
    .then(res => {
      delCancelToken(cancelTokenKey);
      dispatch({
        type: actionTypes.SUCCESS_API_UPDATE_FAVORITE,
        payload: res.data,
      });
    })
    .catch(thrown => {
      delCancelToken(cancelTokenKey);
      if (!axios.isCancel(thrown)) {
        handleErrorResponse(thrown, dispatch);
      }
    });
};
